/**
 * Hook para operações de Excel (Import/Export) da contagem
 *
 * Responsabilidades:
 * - Upload de Excel de itens
 * - Download de Excel de itens
 * - Import de informações da contagem via Excel
 * - Gerenciamento de estados de upload/download
 */
import React, { useState, useCallback } from 'react';
import contagemService from '../services/contagemService';
import jobService from '../services/jobService';
import { msg, MESSAGES } from '../utils/messages';
import toast from 'react-hot-toast';
import type { JobFormData } from './useContagemJobLoader';

// Tipo para erro estruturado (Epic 51)
export type ImportError = {
  error_type: string;
  error: string;
  details?: Record<string, unknown>;
  sugestao?: string;
} | string | null;

export interface UseContagemExcelReturn {
  // States
  uploading: boolean;
  downloading: boolean;
  uploadingInformacoes: boolean;
  selectedFile: File | null;
  selectedInformacoesFile: File | null;
  error: ImportError;

  // Setters
  setSelectedFile: (file: File | null) => void;
  setSelectedInformacoesFile: (file: File | null) => void;
  setError: (error: ImportError) => void;

  // Handlers
  handleUploadExcel: () => Promise<void>;
  handleDownloadExcel: () => Promise<void>;
  handleImportInformacoesExcel: () => Promise<void>;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleInformacoesFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface ExcelConfig {
  id: string | undefined;
  jobClientes: any[];
  selectedImportClient: number | null;
  selectedRegime: boolean | null;
  setSelectedImportClient: (id: number | null) => void;
  setSelectedRegime: (regime: boolean | null) => void;
  setUploadResult: (result: any) => void;
  closeImportModal: () => void;
  closeImportInformacoesModal: () => void;
  setFormData: React.Dispatch<React.SetStateAction<JobFormData>>;
  setLocaisContagemSelecionados?: (locais: any[]) => void;  // Epic 41: Atualizar locais após import
  onPosContagemImportSuccess?: () => void;  // Epic Redesign Pós-Contagem: Callback após import com sucesso
}

export function useContagemExcel(config: ExcelConfig): UseContagemExcelReturn {
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploadingInformacoes, setUploadingInformacoes] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedInformacoesFile, setSelectedInformacoesFile] = useState<File | null>(null);
  const [error, setError] = useState<ImportError>(null);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  }, []);

  const handleInformacoesFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedInformacoesFile(file);
    }
  }, []);

  const handleUploadExcel = useCallback(async () => {
    const {
      id,
      jobClientes,
      selectedImportClient,
      selectedRegime,
      setSelectedImportClient,
      setSelectedRegime,
      setUploadResult,
      closeImportModal,
    } = config;

    if (!selectedFile) {
      msg.warning(MESSAGES.COUNTING.SELECT_FILE_FIRST);
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!id || !token || id === 'new') {
      msg.warning(MESSAGES.COUNTING.SAVE_BEFORE_IMPORT);
      return;
    }

    // Client selection validation
    if (jobClientes.length > 1 && !selectedImportClient) {
      msg.warning("⚠️ Por favor, selecione o cliente para continuar a importação.");
      return;
    }

    // Regime selection validation
    let activeClient = null;
    if (jobClientes.length === 1) activeClient = jobClientes[0];
    else if (selectedImportClient) activeClient = jobClientes.find((c: any) => c.id === selectedImportClient);

    if (activeClient && activeClient.com_regime && activeClient.sem_regime && selectedRegime === null) {
      msg.warning("⚠️ Este cliente permite 'Com Regime' e 'Sem Regime'. Por favor, selecione qual opção utilizar nesta importação.");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const result = await contagemService.uploadExcelEpic19(
        parseInt(id),
        selectedFile,
        selectedImportClient || undefined,
        selectedRegime !== null ? selectedRegime : undefined
      );

      // Story 23.1: Handle confirm_replace response
      if ('confirm_replace' in result && result.confirm_replace) {
        const confirmed = window.confirm(
          `${result.message}\n\nDeseja substituir os ${result.existing_count} itens existentes?`
        );
        if (confirmed) {
          // Re-submit with force_replace=true
          const replaceResult = await contagemService.uploadExcelEpic19(
            parseInt(id),
            selectedFile,
            selectedImportClient || undefined,
            selectedRegime !== null ? selectedRegime : undefined,
            true // force_replace
          );
          if (!('confirm_replace' in replaceResult)) {
            setUploadResult(replaceResult);
            closeImportModal();
            setSelectedFile(null);
            setSelectedImportClient(null);
            setSelectedRegime(null);
            msg.success(
              `Itens substituídos! Total: ${replaceResult.total_linhas}, Processadas: ${replaceResult.total_processadas}, Erros: ${replaceResult.total_erros}`
            );
          }
        } else {
          msg.info('Importação cancelada.');
        }
        return;
      }

      // Normal success response (confirm_replace case already returned above)
      const importResult = result as import('../services/contagemService').ContagemDadosImportados;
      setUploadResult(importResult);
      closeImportModal();
      setSelectedFile(null);
      setSelectedImportClient(null);
      setSelectedRegime(null);

      msg.success(
        `Upload bem-sucedido! Total: ${importResult.total_linhas}, Processadas: ${importResult.total_processadas}, Erros: ${importResult.total_erros}, Taxa: ${importResult.taxa_sucesso.toFixed(1)}%`
      );
    } catch (err: any) {
      // Epic 51: Extrair erro estruturado do backend
      const responseData = err.response?.data;

      // Verificar se é erro estruturado (tem error_type)
      if (responseData?.detail && typeof responseData.detail === 'object' && responseData.detail.error_type) {
        // Erro estruturado do backend
        setError(responseData.detail);
        const displayMessage = responseData.detail.error || 'Erro na importação';
        msg.error(displayMessage);
      } else {
        // Fallback para erro string (formato antigo ou erro genérico)
        const errorMessage = responseData?.detail || err.message || 'Erro ao fazer upload do Excel';
        setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
        msg.error(MESSAGES.COUNTING.UPLOAD_ERROR(typeof errorMessage === 'string' ? errorMessage : 'Erro na importação'));
      }
    } finally {
      setUploading(false);
    }
  }, [config, selectedFile]);

  const handleDownloadExcel = useCallback(async () => {
    const { id } = config;

    const token = localStorage.getItem('accessToken');
    if (!id || !token || id === 'new') {
      msg.warning(MESSAGES.COUNTING.SAVE_BEFORE_DOWNLOAD);
      return;
    }

    setDownloading(true);
    setError(null);

    try {
      await contagemService.downloadAndSaveExcel(parseInt(id));
      msg.success(MESSAGES.COUNTING.DOWNLOAD_SUCCESS);
    } catch (err: any) {
      if (err.message.includes('404') || err.message.includes('não encontrado')) {
        msg.warning(MESSAGES.COUNTING.DOWNLOAD_NO_DATA);
      } else {
        setError(err.message || 'Erro ao fazer download do Excel');
        msg.error(MESSAGES.COUNTING.DOWNLOAD_ERROR(err.message));
      }
    } finally {
      setDownloading(false);
    }
  }, [config]);

  const handleImportInformacoesExcel = useCallback(async () => {
    const { id, closeImportInformacoesModal, onPosContagemImportSuccess } = config;

    if (!id || id === 'new') {
      msg.error('Salve a contagem antes de importar dados');
      return;
    }

    if (!selectedInformacoesFile) {
      msg.warning('Selecione um arquivo Excel primeiro');
      return;
    }

    setUploadingInformacoes(true);
    const loadingToast = msg.loading('Importando pós-contagem por cliente...');

    try {
      // Epic Redesign Pós-Contagem: Use new endpoint for per-client import
      const result = await contagemService.importPosContagemExcel(parseInt(id), selectedInformacoesFile);

      toast.dismiss(loadingToast);

      const { criados, atualizados, erros } = result.resultados;

      if (erros && erros.length > 0) {
        // Show success with warnings
        msg.success(`Importação concluída: ${criados} criados, ${atualizados} atualizados`);
        setTimeout(() => {
          toast(`${erros.length} erro(s) encontrado(s). Verifique os dados.`, { duration: 5000, icon: '⚠️' });
        }, 500);
        console.log('[PosContagem] Erros:', erros);
      } else {
        msg.success(result.message || 'Pós-contagem importada com sucesso!');
      }

      closeImportInformacoesModal();
      setSelectedInformacoesFile(null);

      // Epic Redesign Pós-Contagem: Trigger table refresh after successful import
      onPosContagemImportSuccess?.();

    } catch (err: any) {
      toast.dismiss(loadingToast);
      console.error('Erro ao importar pós-contagem:', err);

      // Extract error message
      const responseData = err.response?.data;
      const errorMessage = responseData?.detail || err.message || 'Erro ao importar arquivo Excel';
      msg.error(typeof errorMessage === 'string' ? errorMessage : 'Erro na importação');
    } finally {
      setUploadingInformacoes(false);
    }
  }, [config, selectedInformacoesFile]);

  return {
    uploading,
    downloading,
    uploadingInformacoes,
    selectedFile,
    selectedInformacoesFile,
    error,
    setSelectedFile,
    setSelectedInformacoesFile,
    setError,
    handleUploadExcel,
    handleDownloadExcel,
    handleImportInformacoesExcel,
    handleFileSelect,
    handleInformacoesFileSelect,
  };
}
